import * as glide from "./glide";

// Cloudflare Worker endpoint for Azure Translator proxy
const TRANSLATE_ENDPOINT = "https://silent-fire-a4ef.ahmed-abied.workers.dev/translate";

// In-memory cache for translations
const cache = new Map<string, string>();

/**
 * Check if text contains Arabic characters
 */
function isArabic(text: string): boolean {
    return /[\u0600-\u06FF]/.test(text);
}

/**
 * Title case helper for English output
 */
function titleCase(str: string): string {
    if (!str) return "";
    return str
        .toLowerCase()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
}

/**
 * Clean input text — trim whitespace and normalize spaces
 */
function cleanText(text: string): string {
    return text.trim().replace(/\s+/g, " ");
}

/**
 * Main translation function with caching and timeout
 */
async function translate(text: string, from: string, to: string): Promise<string> {
    if (!text || !text.trim()) return "";

    const cleaned = cleanText(text);

    // Check cache
    const cacheKey = `${from}:${to}:${cleaned}`;
    if (cache.has(cacheKey)) {
        return cache.get(cacheKey)!;
    }

    try {
        // Create an AbortController for timeout
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 10000); // 10s timeout

        const response = await fetch(TRANSLATE_ENDPOINT, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                text: cleaned,
                from: from,
                to: to,
            }),
            signal: controller.signal,
        });

        clearTimeout(timeout);

        if (!response.ok) {
            console.error("Translation failed:", response.status);
            return text; // Fallback to original
        }

        const data = await response.json();

        // Worker returns { results: ["translated text"] }
        if (data.results && Array.isArray(data.results) && data.results.length > 0) {
            const result = data.results[0];
            cache.set(cacheKey, result);
            return result;
        }

        return text; // Fallback
    } catch (error: any) {
        if (error.name === "AbortError") {
            console.error("Translation timed out after 10s");
        } else {
            console.error("Translation error:", error);
        }
        return text; // Fallback to original
    }
}

const translateIcon = `<svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M24 4C12.954 4 4 12.954 4 24C4 35.046 12.954 44 24 44C35.046 44 44 35.046 44 24C44 12.954 35.046 4 24 4ZM20 36.5V11.5L36 24L20 36.5Z" fill="currentColor"/>
</svg>`;

export default glide.column({
    name: "Name Translator",
    description: "Translates names between Arabic and English using Azure Translator",
    author: "Ahmed Abied",
    about: "Uses Azure Cognitive Services for high-quality name translation. Supports Arabic ↔ English with caching and title case formatting.",
    video: "",
    icon: translateIcon,

    params: {
        language: {
            displayName: "Target Language",
            type: "string",
        },
        name: {
            displayName: "Name",
            type: "string",
        },
    },
    result: { type: "string" },

    async run(language, name) {
        if (language.value === undefined || name.value === undefined) {
            return undefined;
        }

        const targetLang = language.value.toLowerCase().trim();
        const inputName = name.value.trim();

        if (!inputName) {
            return "";
        }

        // Only support en/ar
        if (targetLang !== "en" && targetLang !== "ar") {
            return inputName;
        }

        const inputIsArabic = isArabic(inputName);

        // Already in target language — no translation needed
        if (targetLang === "en" && !inputIsArabic) return inputName;
        if (targetLang === "ar" && inputIsArabic) return inputName;

        // Translate Arabic → English (with title case)
        if (targetLang === "en" && inputIsArabic) {
            const result = await translate(inputName, "ar", "en");
            return titleCase(result);
        }

        // Translate English → Arabic
        if (targetLang === "ar" && !inputIsArabic) {
            return await translate(inputName, "en", "ar");
        }

        return inputName;
    },
});
